import { AuScope, BrandKitVersionDto, hasScope } from "@arrowsup/platform-dtos";
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import { UserContext } from "../../logic/auth/user-context";
import { useActiveBrandKit } from "../../logic/brand-kit/use-brand-kit";
import { BrandAssetLibraryView } from "./brand-asset-library";
import { BrandAssetsProvider } from "./brand-assets";
import { BrandKitDraftEditor } from "./brand-kit-draft-editor";
import { versionName } from "./brand-kit-field-value";
import { BrandKitFieldsView } from "./brand-kit-fields-view";
import { BrandKitHistory } from "./brand-kit-history";

enum BrandKitTab {
  Published = "published",
  Draft = "draft",
  Assets = "assets",
  History = "history",
}

const PublishedView: React.FC<{
  active?: BrandKitVersionDto;
  canUpdate: boolean;
}> = ({ active, canUpdate }) => {
  if (!active) {
    return (
      <Alert severity="info">
        No brand kit has been published yet.
        {canUpdate && " Fill in the draft and publish it."}
      </Alert>
    );
  }

  const at = new Date(active.publishedAt ?? active.updatedAt).toLocaleString();
  const by = active.updatedBy ? ` by ${active.updatedBy}` : "";

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {`Published ${versionName(active)} on ${at}${by}`}
      </Typography>
      <BrandAssetsProvider assets={active.assets}>
        <BrandKitFieldsView fields={active.fields} />
      </BrandAssetsProvider>
    </Stack>
  );
};

export const BrandKitPage: React.FC = () => {
  const { user } = useContext(UserContext);
  const canUpdate = hasScope(user.policies, AuScope.BrandKitUpdate);
  const [tab, setTab] = useState<BrandKitTab>(BrandKitTab.Published);
  const { data: active, isLoading, isError } = useActiveBrandKit();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">Failed to load the brand kit</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1000, margin: "0 auto" }}>
      <Tabs value={tab} onChange={(_, t: BrandKitTab) => setTab(t)}>
        <Tab value={BrandKitTab.Published} label="Published" />
        {canUpdate && <Tab value={BrandKitTab.Draft} label="Draft" />}
        {canUpdate && <Tab value={BrandKitTab.Assets} label="Assets" />}
        <Tab value={BrandKitTab.History} label="History" />
      </Tabs>

      <Box mt={2}>
        {tab === BrandKitTab.Published && (
          <Box role="tabpanel" aria-label="Published">
            <PublishedView active={active} canUpdate={canUpdate} />
          </Box>
        )}
        {/* Stays mounted so unsaved edits survive switching tabs. */}
        {canUpdate && (
          <Box
            role="tabpanel"
            aria-label="Draft"
            hidden={tab !== BrandKitTab.Draft}
          >
            <BrandKitDraftEditor active={active} />
          </Box>
        )}
        {tab === BrandKitTab.Assets && (
          <Box role="tabpanel" aria-label="Assets">
            <BrandAssetLibraryView />
          </Box>
        )}
        {tab === BrandKitTab.History && (
          <Box role="tabpanel" aria-label="History">
            <BrandKitHistory
              canUpdate={canUpdate}
              onRestored={() => setTab(BrandKitTab.Draft)}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
